import { useCallback, useEffect, useState } from 'react';

import {
  createDefaultDashboardLayout,
  loadDashboardLayoutFromStorage,
  saveDashboardLayoutToStorage,
  type DashboardLayoutPrefs,
} from '@/products/helpdesk/features/dashboard/dashboard-layout';
import { dashboardWidgetDefaultEnabledMap } from '@/products/helpdesk/features/dashboard/dashboard-widget-registry';

function layoutForUser(userId: string | undefined): DashboardLayoutPrefs {
  if (!userId) {
    return createDefaultDashboardLayout();
  }
  return loadDashboardLayoutFromStorage(userId, dashboardWidgetDefaultEnabledMap());
}

/** v1: localStorage only. v2 (optional): sync via GET/PUT /api/me/dashboard-layout — not implemented. */
export function useDashboardLayout(userId: string | undefined) {
  const [layout, setLayoutState] = useState<DashboardLayoutPrefs>(() => layoutForUser(userId));
  const [hydratedFor, setHydratedFor] = useState<string | null>(() => userId ?? null);

  // Re-hydrate when the signed-in user changes (React: adjust state while rendering).
  const [prevUserId, setPrevUserId] = useState(userId);
  if (userId !== prevUserId) {
    setPrevUserId(userId);
    setLayoutState(layoutForUser(userId));
    setHydratedFor(userId ?? null);
  }

  const ready = Boolean(userId && hydratedFor === userId);

  useEffect(() => {
    if (!userId || hydratedFor !== userId) {
      return;
    }
    saveDashboardLayoutToStorage(userId, layout);
  }, [hydratedFor, userId, layout]);

  const setLayout = useCallback(
    (next: DashboardLayoutPrefs | ((prev: DashboardLayoutPrefs) => DashboardLayoutPrefs)) => {
      setLayoutState((prev) => (typeof next === 'function' ? next(prev) : next));
    },
    [],
  );

  const resetLayout = useCallback(() => {
    setLayoutState(createDefaultDashboardLayout());
  }, []);

  return {
    layout,
    setLayout,
    resetLayout,
    ready,
  };
}
